import React, { useRef, useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FixedSizeList as List } from "react-window";

// PDF.js 워커 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function PdfViewerModal({
                                           // PDF 파일 경로 (URL, 혹은 Blob/파일객체도 가능)
                                           path,
                                           // 하이라이트할 검색어 (없으면 '' 또는 null)
                                           keyword = "",
                                           // 검색 매칭된 페이지 배열 [{ page: number, contents: string }, ...]
                                           matchedPages = [],
                                           // PDF 로딩 완료 후 부모에게 알려줄 콜백(옵션)
                                           onLoadComplete,
                                           // 모달이므로, 열림/닫힘 여부 & 닫기 함수(옵션)
                                           isOpen = true,
                                           onClose,
                                       }) {
    // PDF 전체 페이지 수
    const [numPages, setNumPages] = useState(null);
    // react-pdf에서 로드된 PDF 문서 객체 (v5.x 기준)
    const [pdf, setPdf] = useState(null);

    // 컨테이너 높이/너비 파악용 ref
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // react-window 리스트 ref → 특정 페이지로 스크롤 이동에 사용
    const listRef = useRef(null);

    // matchedPages 정렬
    const sortedMatchedPages = [...(matchedPages || [])].sort((a, b) => a.page - b.page);

    // PDF 로딩 완료시 호출
    function onDocumentLoadSuccess(pdfDoc) {
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);
        if (onLoadComplete) {
            onLoadComplete(); // 부모 콜백
        }
    }

    // 컨테이너 리사이즈 -> PDF width 재계산
    useEffect(() => {
        function updateWidth() {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        }
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    // PDF 로드 후, 매칭된 페이지가 있으면 첫 페이지로 자동 스크롤
    useEffect(() => {
        if (numPages && sortedMatchedPages.length > 0 && listRef.current) {
            const firstMatched = sortedMatchedPages[0].page;
            // react-window의 scrollToItem(인덱스, align)
            listRef.current.scrollToItem(firstMatched - 1, "center");
        }
    }, [numPages, path]);

    // 썸네일 strip에서 클릭 시 → 해당 페이지로 스크롤
    const scrollToPage = (pageNum) => {
        if (listRef.current) {
            listRef.current.scrollToItem(pageNum - 1, "start");
        }
    };

    // “텍스트 레이어”에 검색어 하이라이트 (onGetTextLayerSuccess)
    const highlightTextLayer = (textLayerDiv) => {
        if (!keyword || !textLayerDiv) return;
        const escaped = keyword.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(${escaped})`, "gi");

        Array.from(textLayerDiv.querySelectorAll("span")).forEach((span) => {
            if (span.textContent && regex.test(span.textContent)) {
                span.innerHTML = span.textContent.replace(
                    regex,
                    `<mark style="background: yellow;">$1</mark>`
                );
            }
        });
    };

    // ★ matchedPages 빠른 확인용
    const matchedPageSet = new Set(sortedMatchedPages.map((p) => p.page));

    // rowRenderer for react-window
    // index: 0-based, => pageNumber = index + 1
    function PdfPageRow({ index, style, data }) {
        const { pdf, pageWidth, highlightKeyword, matchedPageSet } = data;
        const pageNumber = index + 1;
        const isMatched = matchedPageSet.has(pageNumber);

        return (
            <div
                style={style} // position, height 등
                className="flex justify-center items-start py-4"
            >
                <div
                    className={`relative ${
                        isMatched
                            ? "border-4 border-blue-400 bg-blue-50 shadow-lg rounded-2xl"
                            : ""
                    }`}
                >
                    <Page
                        pdf={pdf}
                        pageNumber={pageNumber}
                        width={pageWidth}
                        renderAnnotationLayer={false}
                        renderTextLayer={true}
                        onGetTextLayerSuccess={highlightTextLayer}
                    />
                </div>
            </div>
        );
    }

    // 모달이 아니거나, 열리지 않은 상태라면 null
    // (isOpen/onClose가 꼭 필요없다면 이 조건은 빼도 됩니다)
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* (A) 배경 오버레이 */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => onClose && onClose()}
            />

            {/* (B) 모달 컨테이너 */}
            <div className="relative bg-white w-full max-w-[80%] h-[90vh] rounded-lg shadow-lg flex flex-col">
                {/* 헤더 부분 */}
                <div className="flex items-center justify-between p-2 border-b">
                    <h2 className="text-lg font-semibold">PDF 미리보기</h2>
                    <button
                        className="text-2xl leading-none text-gray-500 hover:text-gray-700"
                        onClick={() => onClose && onClose()}
                    >
                        &times;
                    </button>
                </div>

                {/* (1) 키워드 정보 + 매칭 페이지 썸네일 strip */}
                {keyword && (
                    <div className="px-3 pt-2 text-sm text-gray-600">
                        “<strong>{keyword}</strong>” 포함된 페이지:
                    </div>
                )}
                {sortedMatchedPages.length > 0 && (
                    <div
                        className="flex flex-row overflow-x-auto gap-4 mb-2 py-3 px-3 border-b bg-gray-50"
                        style={{ maxHeight: 140 }}
                    >
                        {sortedMatchedPages.map((mp) => (
                            <div
                                key={mp.page}
                                className="flex flex-col items-center min-w-[130px] max-w-[150px]"
                            >
                                <button
                                    className="border-2 border-blue-400 bg-white rounded-md shadow hover:shadow-lg p-1 flex flex-col items-center"
                                    onClick={() => scrollToPage(mp.page)}
                                    title={`${mp.page}쪽으로 이동`}
                                >
                                    {/* (A) 썸네일 미리보기 */}
                                    <Document file={path}>
                                        <Page
                                            pageNumber={mp.page}
                                            width={120}
                                            renderAnnotationLayer={false}
                                            renderTextLayer={false}
                                        />
                                    </Document>
                                    <div className="text-blue-700 text-xs font-bold mt-1">
                                        {mp.page}쪽
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* (2) 실제 PDF 가상 스크롤 부분 */}
                <div
                    ref={containerRef}
                    className="flex-1 overflow-hidden bg-white w-full"
                    style={{ minHeight: 0 }}
                >
                    {path ? (
                        <Document
                            file={path}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<div className="text-center p-4">PDF 다운로드중...</div>}
                            error={
                                <div className="text-center p-4 text-red-500">PDF 로딩 오류</div>
                            }
                            noData={
                                <div className="text-center p-4 text-gray-400">
                                    PDF 파일이 없습니다
                                </div>
                            }
                        >
                            {pdf && numPages && (
                                <List
                                    ref={listRef}
                                    // 모달 높이(헤더+썸네일strip 제외) 고려
                                    height={Math.max(200,  // 최소값
                                        // 대략 모달 전체높이 - 썸네일strip - 헤더 등
                                        800)}
                                    itemCount={numPages}
                                    // 페이지 하나의 높이 (A4 기준 + 여백 감안. 필요 시 조정)
                                    itemSize={900}
                                    width={"100%"}
                                    overscanCount={2}
                                    itemData={{
                                        pdf,
                                        pageWidth: Math.max(640, containerWidth * 0.98), // min 640, max containerWidth
                                        highlightKeyword: keyword,
                                        matchedPageSet,
                                    }}
                                    style={{ background: "#fff" }}
                                >
                                    {PdfPageRow}
                                </List>
                            )}
                        </Document>
                    ) : (
                        <div className="p-4 text-center text-gray-500">
                            PDF 경로(path)가 없습니다.
                        </div>
                    )}
                </div>

                {/* (3) 하단 버튼들 */}
                <div className="p-2 border-t flex justify-end gap-2">
                    <a
                        href={path}
                        download
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        다운로드
                    </a>
                    <button
                        onClick={() => onClose && onClose()}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
}